import React, { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * ForgotPassword — request a reset link via email.
 *
 * Server always returns 200 regardless of whether the email exists
 * (anti-enumeration). We show the same success state either way — the
 * user is instructed to check their inbox even if the email is unknown.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-zinc-50 dark:bg-zinc-950" data-testid="forgot-root">
      <div className="pointer-events-none absolute inset-x-0 -top-40 -z-0 h-[420px] bg-[radial-gradient(circle_at_50%_0%,rgba(16,185,129,0.18),transparent_70%)]" aria-hidden="true"/>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col items-center px-6 py-12 sm:py-16">
        <Link to="/" className="flex items-center gap-2">
          <Logo className="h-9 w-9" />
          <span className="font-heading text-lg font-bold">Syllox</span>
        </Link>

        <div className="mt-10 w-full">
          {sent ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center dark:border-emerald-900/40 dark:bg-emerald-950/20" data-testid="forgot-sent-card">
              <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-white">
                <MailCheck size={22}/>
              </div>
              <h1 className="font-heading text-2xl font-bold" data-testid="forgot-sent-heading">Check your inbox.</h1>
              <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
                If an account exists for <span className="font-medium">{email || "that email"}</span>, we&rsquo;ve sent a password-reset link. The link expires in 30 minutes.
              </p>
              <p className="mt-4 text-xs text-zinc-500">
                Didn&rsquo;t get it? Check your spam folder or{" "}
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="font-medium text-emerald-600 hover:underline"
                  data-testid="forgot-retry-btn"
                >
                  try a different email
                </button>.
              </p>
              <Link to="/login" className="mt-6 inline-block text-xs font-medium text-emerald-600 hover:underline">
                ← Back to login
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl" data-testid="forgot-heading">
                Forgot password?
              </h1>
              <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400" data-testid="forgot-subheading">
                Enter your email — we&rsquo;ll send you a link to set a new one.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-3" data-testid="forgot-form">
                <div className="space-y-1.5">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 bg-emerald-500 text-white hover:bg-emerald-600"
                  data-testid="forgot-submit-btn"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin"/> : "Send reset link"}
                </Button>
              </form>

              <p className="mt-5 text-center text-sm text-zinc-600 dark:text-zinc-400">
                Remembered it?{" "}
                <Link to="/login" className="font-medium text-emerald-600 hover:underline">
                  Log in
                </Link>
              </p>
            </>
          )}
        </div>

        <div className="mt-auto pt-8 text-center text-xs text-zinc-400 dark:text-zinc-500">
          © {new Date().getFullYear()} Syllox · Made in India
        </div>
      </div>
    </div>
  );
}
